import type { LogEntry } from '@/lib/logger';

export interface SerializedError {
  name: string;
  message: string;
  stack?: string;
}

export interface LogTimelineEntry {
  at: string;
  source: 'logger' | 'window.onerror' | 'unhandledrejection';
  level: 'debug' | 'info' | 'warn' | 'error';
  message: string;
  error?: SerializedError;
  context?: Record<string, unknown>;
}

/** Defensive cap so a single stack cannot dominate the payload sent to the server. */
const MAX_STACK_CHARS = 4000;

export function serializeError(value: unknown): SerializedError | undefined {
  if (value == null) {
    return undefined;
  }
  if (value instanceof Error) {
    return {
      name: value.name,
      message: value.message,
      ...(value.stack ? { stack: value.stack.slice(0, MAX_STACK_CHARS) } : {}),
    };
  }
  return { name: 'NonError', message: String(value) };
}

/**
 * The whole bundle is JSON.stringify'd for POST and preview, so a circular
 * ref / BigInt / DOM node in context would throw and lose the entire report.
 */
function jsonSafeContext(
  context: Record<string, unknown> | undefined
): Record<string, unknown> | undefined {
  if (!context || Object.keys(context).length === 0) {
    return undefined;
  }
  try {
    return JSON.parse(JSON.stringify(context)) as Record<string, unknown>;
  } catch {
    const safe: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(context)) {
      try {
        JSON.stringify(value);
        safe[key] = value;
      } catch {
        try {
          safe[key] = String(value);
        } catch {
          safe[key] = '[unserializable]';
        }
      }
    }
    return safe;
  }
}

export function serializeLoggerEntries(
  entries: LogEntry[]
): LogTimelineEntry[] {
  return entries.map(entry => {
    const { error: rawError, ...rest } = entry.context ?? {};
    const error = serializeError(rawError);
    const context = jsonSafeContext(rest as Record<string, unknown>);

    return {
      at: entry.timestamp,
      source: 'logger',
      level: entry.level,
      message: entry.message,
      ...(error ? { error } : {}),
      ...(context ? { context } : {}),
    };
  });
}
